<template>
    <div class="modal" v-if="localEvent" @click.self="handleBackdropClick">
        <div class="modal-content">
            <div v-if="isLoading" class="loading-spinner">
                Loading...
            </div>
            <template v-else>
                <h3>Event Details</h3>
                
                <!-- Event Information -->
                <div class="detail-section">
                    <h4>Event Information</h4>
                    <div class="event-info-grid">
                        <div class="info-item">
                            <div class="detail-label">Date:</div>
                            <div class="detail-value">{{ formatDateFull(localEvent.event_date) }}</div>
                        </div>
                        <div class="info-item">
                            <div class="detail-label">Start Time:</div>
                            <div class="detail-value">{{ formatTime(localEvent.event_date) }}</div>
                        </div>
                        <div class="info-item">
                            <div class="detail-label">End Time:</div>
                            <div class="detail-value">{{ formatTime(localEvent.end_time) }}</div>
                        </div>
                        <div class="info-item">
                            <div class="detail-label">Event Type:</div>
                            <div class="detail-value">{{ localEvent.event_type }}</div>
                        </div>
                        <div class="info-item">
                            <div class="detail-label">Venue:</div>
                            <div class="detail-value">{{ localEvent.venue_name }}</div>
                        </div>

                        <!-- Show these sections only for Regular Meetings -->
                        <template v-if="localEvent.event_type === 'Regular Meeting'">
                            <div class="info-item">
                                <div class="detail-label">Format:</div>
                                <div class="detail-value">{{ getFormatName(localEvent.format_id) }}</div>
                            </div>
                        </template>
                    </div>
                </div>

                <div class="detail-section">
                    <h4>Speaker Information</h4>
                    <div class="details-container">
                        <!-- Discussion Sessions - Two columns or centered single column -->
                        <div class="sessions-grid" :class="[
                            discussionSessions.length > 1 ? 'two-columns' : 'single-column',
                            discussionSessions.length === 1 ? 'center-single' : ''
                        ]">
                            <template v-for="session in discussionSessions" :key="session.id">
                                <div class="session-card" :class="getSessionClass(session)">
                                    <div class="session-header">
                                        {{ getSessionHeader(session) }}
                                    </div>
                                    <div class="session-content">
                                        <div class="topic">{{ session.topic }}</div>
                                        <div class="description">{{ session.description }}</div>
                                    </div>
                                </div>
                            </template>
                        </div>

                        <!-- Member/Guest Speaker Sessions - Single column -->
                        <div class="sessions-grid single-column center-single">
                            <template v-for="session in memberSpeakerSessions" :key="session.id">
                                <div class="session-card" :class="getSessionClass(session)">
                                    <div class="session-header">
                                        {{ getSessionHeader(session) }}
                                    </div>
                                    <div class="session-content">
                                        <div class="speaker-name">
                                            {{ session.session_type === 'external_speaker' 
                                                ? session.guest_speaker_name 
                                                : getEventSpeakerName(session.speaker_id) }}
                                        </div>
                                        <div class="topic">{{ session.topic }}</div>
                                        <div class="description">{{ session.description }}</div>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>

                <!-- Attendance Section (shown for all event types) -->
                <div class="attendance-section">
                    <h3>Attendance</h3>
                    
                    <div class="attendance-group">
                        <div class="attendance-label">Attending:</div>
                        <div class="attendance-value">
                            {{ getAttendingMembers() }}
                        </div>
                    </div>

                    <div class="attendance-group">
                        <div class="attendance-label">Not Attending:</div>
                        <div class="attendance-value">
                            {{ getNotAttendingMembers() }}
                        </div>
                    </div>

                    <div class="attendance-group">
                        <div class="attendance-label">No Response:</div>
                        <div class="attendance-value">
                            {{ getNoResponseMembers() }}
                        </div>
                    </div>

                    <div v-if="substitutes.length > 0" class="attendance-group">
                        <div class="attendance-label">Substitutes:</div>
                        <div class="attendance-value">
                            <div v-for="sub in substitutes" :key="sub.id">
                                {{ sub.name }} (for {{ sub.host_name }})
                            </div>
                        </div>
                    </div>

                    <div v-if="visitors.length > 0" class="attendance-group">
                        <div class="attendance-label">Visitors:</div>
                        <div class="attendance-value">
                            <div v-for="visitor in visitors" :key="visitor.id">
                                {{ visitor.name }} (hosted by {{ visitor.host_name }})
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-actions">
                    <button @click="$emit('close')" class="close-btn">Close</button>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch } from 'vue';

export default {
  name: 'EventDetailsModal',
  props: {
    event: {
      type: Object,
      required: true
    },
    currentUser: {
      type: Object,
      required: false,
      default: null
    },
    isEditing: {
      type: Boolean,
      required: false,
      default: false
    },
    formats: {
      type: Array,
      default: () => []
    }
  },
  setup(props, { emit }) {
    const members = ref([]);
    const isLoading = ref(true);
    const eventVisitors = ref([]);
    const localEvent = ref(null);

    // Watch for changes to props.event and update localEvent
    watch(() => props.event, (newEvent) => {
        if (newEvent) {
            localEvent.value = { ...newEvent };
            isLoading.value = false;
        }
    }, { immediate: true });


    const loadEventDetails = async () => {
        isLoading.value = true;
        try {
            localEvent.value = props.event;
        } catch (error) {
            console.error('[Component] Error loading event details:', error);
        } finally {
            isLoading.value = false;
        }
    };

    const getAttendingMembers = () => {
        if (!localEvent.value?.attendees) {
            return 'None';
        }

        const attending = localEvent.value.attendees
            .filter(a => a.attending === true)
            .map(a => a.name)
            .join(', ');
        
        return attending || 'None';
    };

    const getNotAttendingMembers = () => {
        if (!localEvent.value?.attendees) {
            return 'None';
        }

        const notAttending = localEvent.value.attendees
            .filter(a => a.attending === false)
            .map(a => a.name)
            .join(', ');
        
        return notAttending || 'None';
    };

    const getNoResponseMembers = () => {
        if (!localEvent.value?.attendees) {
            return 'None';
        }

        const noResponse = localEvent.value.attendees
            .filter(a => a.attending === null)
            .map(a => a.name)
            .join(', ');
        
        return noResponse || 'None';
    };

    const getAttendanceGroups = (attendees) => {
        if (!attendees) return {
            visitors: [],
            substitutes: []
        };

        const allVisitors = attendees.reduce((acc, member) => {
            if (member.visitors && member.visitors.length > 0) {
                member.visitors.forEach(visitor => {
                    acc.push({
                        ...visitor,
                        host_name: member.name
                    });
                });
            }
            return acc;
        }, []);

        return {
            visitors: allVisitors.filter(v => v.type === 'visitor'),
            substitutes: allVisitors.filter(v => v.type === 'substitute')
        };
    };

    const visitors = computed(() => {
        const { visitors } = getAttendanceGroups(localEvent.value?.attendees);
        return visitors;
    });

    const substitutes = computed(() => {
        const { substitutes } = getAttendanceGroups(localEvent.value?.attendees);
        return substitutes;
    });

    const speakerSessions = computed(() => {
        if (!props.event?.sessions) return [];
        
        // Get all sessions sorted by speaker_order
        const allSessions = props.event.sessions
            .filter(session => 
                session.session_type === 'member_speaker' || 
                session.session_type === 'external_speaker' ||
                session.session_type === 'discussion'
            )
            .sort((a, b) => a.speaker_order - b.speaker_order);

        // Find the format to get segment names
        const format = props.formats.find(f => f.id === props.event.format_id);
        
        // Add segment names to sessions
        return allSessions.map(session => {
            const matchingSegment = format?.segments?.find(
                segment => segment.segment_type === session.session_type && 
                          segment.order_index === (session.speaker_order - 1)
            );
            return {
                ...session,
                segmentName: matchingSegment?.name || ''
            };
        });
    });

    // Separate computed properties for different session types
    const discussionSessions = computed(() => 
        speakerSessions.value.filter(s => s.session_type === 'discussion')
    );

    const memberSpeakerSessions = computed(() => 
        speakerSessions.value.filter(s => 
            s.session_type === 'member_speaker' || 
            s.session_type === 'external_speaker'
        )
    );

    const getSessionClass = (session) => {
        switch(session.session_type) {
            case 'external_speaker':
                return 'guest-speaker';
            case 'member_speaker':
                return 'member-speaker';
            case 'discussion':
                return 'discussion-session';
            default:
                return '';
        }
    };

    const getSessionHeader = (session) => {
        // Always use the segment name if available
        if (session.segmentName) {
            return session.segmentName;
        }
        // Fallback to generic names if segment name not found
        switch(session.session_type) {
            case 'external_speaker':
                return 'Guest Speaker';
            case 'member_speaker':
                return 'Member Speaker';
            default:
                return '';
        }
    };

    const sessionGridClass = computed(() => {
        return speakerSessions.value.length > 1 ? 'two-columns' : 'single-column';
    });

    const formatDateFull = (date) => {
        if (!date) return '';
        return new Date(date).toLocaleDateString('en-US', {
            weekday: 'short',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatTime = (date) => {
        if (!date) return '';
        return new Date(date).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const getEventTypeClass = (type) => {
        switch (type) {
            case 'Regular Meeting':
                return 'event-type-regular';
            case 'Network Event':
                return 'event-type-network';
            case 'Social Event':
                return 'event-type-social';
            default:
                return '';
        }
    };

    const getEventSpeakerName = (speakerId) => {
        const sessions = localEvent.value?.sessions || [];
        const session = sessions.find(s => s.speaker_id === speakerId);
        return session?.speaker_name || 'Not assigned';
    };

    const getFormatName = (formatId) => {
        if (!formatId) return 'None';
        const format = props.formats.find(f => f.id === formatId);
        return format?.name || 'None';
    };

    const handleBackdropClick = () => {
        emit('close');
    };

    return {
      localEvent,
      eventVisitors,
      members,
      isLoading,
      getAttendingMembers,
      getNotAttendingMembers,
      getNoResponseMembers,
      formatDateFull,
      formatTime,
      getEventTypeClass,
      getEventSpeakerName,
      loadEventDetails,
      substitutes,
      visitors,
      handleBackdropClick,
      sessionGridClass,
      speakerSessions,
      discussionSessions,
      memberSpeakerSessions,
      getSessionClass,
      getSessionHeader,
      getFormatName
    };
  }
};
</script>

<style scoped>
.modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 0;
    overflow-y: auto;
    z-index: 1000;
}

.modal-content {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    width: 100%;
    max-width: 900px;
    max-height: 100vh;
    overflow-y: auto;
    position: relative;
}

.sessions-grid {
    display: grid;
    gap: 0.75rem;
    margin-top: 0.75rem;
}

.sessions-grid.single-column {
    grid-template-columns: 1fr;
}

.sessions-grid.two-columns {
    grid-template-columns: repeat(2, 1fr);
}

.sessions-grid.center-single {
    display: flex;
    justify-content: center;
}

.sessions-grid.center-single .session-card {
    width: 100%;
    max-width: 500px;
}

@media (max-width: 768px) {
    .sessions-grid.two-columns {
        grid-template-columns: 1fr;
    }
}

.detail-section {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 1rem;
    margin-top: 1rem;
}

.detail-section h4 {
    margin: 0 0 0.75rem 0;
    color: #2c3e50;
    font-size: 1.1rem;
    text-align: center;
    padding-left: 0;
}

.event-info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 0.5rem;
    margin-bottom: 1rem;
}

.info-item {
    display: flex;
    align-items: baseline;
    gap: 1rem;
}

.info-item .detail-label {
    min-width: 90px;
    color: #666;
    font-weight: 500;
    font-size: 0.9rem;
    text-align: right;
    flex-shrink: 0;
}

.info-item .detail-value {
    flex: 1;
    min-width: 0;
}

.detail-value:empty::after {
    content: 'None';
    color: #666;
    font-style: italic;
}

.detail-value.description-container {
    grid-column: 2;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.event-type {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 1rem;
    font-size: 0.9rem;
    font-weight: 500;
}

.event-type-regular-meeting {
    background-color: #e3f2fd;
    color: #1976d2;
}

.event-type-social {
    background-color: #f3e5f5;
    color: #7b1fa2;
}

.event-type-network {
    background-color: #e8f5e9;
    color: #2e7d32;
}

.event-type-special {
    background-color: #fff3e0;
    color: #e65100;
}

.modal-actions {
    margin-top: 1.5rem;
    display: flex;
    justify-content: flex-end;
}

.close-btn {
    padding: 0.5rem 1rem;
    background-color: #e9ecef;
    border: none;
    border-radius: 4px;
    color: #495057;
    cursor: pointer;
    font-size: 0.9rem;
    transition: background-color 0.2s;
}

.close-btn:hover {
    background-color: #dee2e6;
}

.editable-input {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9rem;
}

.editable-textarea {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    min-height: 80px;
    resize: vertical;
    font-size: 0.9rem;
    background: white;
}

.session-actions {
    margin-top: 0.5rem;
    display: flex;
    justify-content: flex-end;
}

.save-btn {
    background: #4caf50;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
}

.save-btn:hover {
    background: #45a049;
}

.intro-topic,
.topic-label,
.topic-value {
    display: none;
}

.session-card {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    margin-bottom: 0.75rem;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
}

.session-header {
    padding: 0.5rem 1rem;
    font-weight: 500;
    font-size: 1rem;
    text-align: center;
}

.session-content {
    padding: 0.75rem 1rem;
}

.speaker-name {
    font-size: 1.1rem;
    font-weight: 500;
    color: #2c3e50;
    margin-bottom: 0.5rem;
    text-align: center;
}

.topic {
    color: #495057;
    margin-bottom: 0.5rem;
    font-weight: 500;
}

.description {
    color: #6c757d;
    font-size: 0.95rem;
    line-height: 1.4;
}

/* Session type specific styles */
.session-card.member-speaker .session-header {
    background: #e3f2fd;
    color: #1976d2;
}

.session-card.guest-speaker .session-header {
    background: #fce4ec;
    color: #c2185b;
}

.session-card.discussion-session .session-header {
    background: #f3e5f5;
    color: #7b1fa2;
}

.attendance-section {
    margin-top: 2rem;
    text-align: left;
}

.attendance-group {
    margin-bottom: 1rem;
    text-align: left;
}

.attendance-label {
    color: #666;
    font-weight: 500;
    font-size: 0.9rem;
    text-align: left;
}

.attendance-value {
    color: #2c3e50;
    text-align: left;
    line-height: 1.5;
    margin-top: 0.25rem;
}

/* Mobile Styles */
@media (max-width: 768px) {
    .modal {
        align-items: stretch;
    }

    .modal-content {
        padding: 1rem;
        border-radius: 0;
        margin: 0;
    }

    .detail-row {
        grid-template-columns: 1fr;
        gap: 0.5rem;
        margin-bottom: 1rem;
    }

    .detail-label {
        color: #2c3e50;
        font-size: 0.9rem;
        font-weight: 600;
        text-align: left;
        padding-top: 0;
        margin-bottom: 0;
    }

    .detail-value {
        font-size: 1rem;
        padding: 0;
        border-bottom: 1px solid #eee;
        padding-bottom: 0.75rem;
        color: #486581;
    }

    .event-info-title {
        text-align: center;
        color: #2c3e50;
        font-size: 1.25rem;
        margin-bottom: 1.5rem;
        font-weight: 500;
    }
}

.details-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-width: 600px;
    margin: 0 auto;
}
</style>